import { useCallback, useEffect, useState } from "react"

// Define theme type to include system option
export type ThemeType = "light" | "dark" | "system"

export interface UseThemeResult {
  /** Current theme setting (light, dark, or system) */
  themeSetting: ThemeType
  /** The actual applied theme (light or dark) */
  activeTheme: "light" | "dark"
  /** Function to toggle between themes */
  toggleTheme: () => void
  /** Function to set a specific theme */
  setTheme: (theme: ThemeType) => void
  /** Function to directly set the theme setting state */
  setThemeSetting: (theme: ThemeType) => void
}

/**
 * Custom hook to manage theme state with support for light, dark, and system preference
 * Updated for Tailwind CSS v4.1 with @theme directives and dark variant
 */
export function useTheme(): UseThemeResult {
  // Track both the active theme setting and the actual applied theme
  const [themeSetting, setThemeSetting] = useState<ThemeType>("system")
  const [activeTheme, setActiveTheme] = useState<"light" | "dark">("light")
  const [mounted, setMounted] = useState(false)

  // Check system preference - memoized to avoid recreating on every render
  const getSystemTheme = useCallback((): "light" | "dark" => {
    if (typeof window === "undefined") return "light"
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light"
  }, [])

  // Apply the theme to the DOM
  const applyTheme = useCallback((theme: "light" | "dark") => {
    if (typeof window === "undefined") return

    // console.log(`[useTheme] Applying theme to DOM: ${theme}`)

    // Clean up any legacy `.dark` classes that may still linger
    document.body.classList.remove("dark")
    document.documentElement.classList.remove("dark")

    // Rely solely on the `data-theme` attribute for theme detection
    // in Tailwind (configured via @custom-variant). Add or update it
    // based on the requested theme.
    if (theme === "dark") {
      document.documentElement.setAttribute("data-theme", "dark")
    } else {
      document.documentElement.setAttribute("data-theme", "light")
    }

    // Explicitly set the `color-scheme` property so native UI controls
    document.documentElement.style.colorScheme = theme
    // console.log(`[useTheme] Set colorScheme to: ${theme}`)
  }, [])

  // Update the applied theme based on theme setting
  const updateActiveTheme = useCallback(
    (setting: ThemeType) => {
      const newActiveTheme =
        setting === "system" ? getSystemTheme() : (setting as "light" | "dark")
      setActiveTheme(newActiveTheme)
      applyTheme(newActiveTheme)
    },
    [getSystemTheme, applyTheme]
  )

  // Initialize theme on mount - only runs once
  useEffect(() => {
    setMounted(true)

    // Get saved theme setting from localStorage
    const savedSetting = localStorage.getItem(
      "themeSetting"
    ) as ThemeType | null
    const initialSetting = savedSetting || "system"

    // console.log(`[useTheme] Initializing with setting: ${initialSetting}`)
    setThemeSetting(initialSetting)
    updateActiveTheme(initialSetting)
  }, [updateActiveTheme])

  // Set up listener for system theme changes
  useEffect(() => {
    if (!mounted) return

    // Listen for system preference changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")

    const handleChange = () => {
      console.log(
        `[useTheme] System theme changed, current setting: ${themeSetting}`
      )
      if (themeSetting === "system") {
        const newSystemTheme = getSystemTheme()
        console.log(`[useTheme] Updating to system theme: ${newSystemTheme}`)
        setActiveTheme(newSystemTheme)
        applyTheme(newSystemTheme)
      }
    }

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [mounted, themeSetting, getSystemTheme, applyTheme])

  // Save theme setting to localStorage when it changes
  useEffect(() => {
    if (!mounted) return
    localStorage.setItem("themeSetting", themeSetting)
  }, [themeSetting, mounted])

  // Toggle between light and dark modes
  const toggleTheme = useCallback(() => {
    // Simple toggle between light and dark (no system)
    const newSetting: ThemeType = activeTheme === "light" ? "dark" : "light"

    console.log(
      `[useTheme] Toggling theme from ${activeTheme} to ${newSetting}`
    )
    setThemeSetting(newSetting)
    setActiveTheme(newSetting) // Directly update activeTheme for immediate UI response
    updateActiveTheme(newSetting) // Apply the theme to DOM elements
  }, [activeTheme, updateActiveTheme])

  // Set a specific theme
  const setTheme = useCallback(
    (theme: ThemeType) => {
      console.log(`[useTheme] Setting theme to ${theme}`)
      setThemeSetting(theme)
      if (theme !== "system") {
        setActiveTheme(theme) // Directly set activeTheme for immediate UI update
      }
      updateActiveTheme(theme) // Apply the theme to DOM elements
    },
    [updateActiveTheme]
  )

  return {
    themeSetting,
    activeTheme,
    toggleTheme,
    setTheme,
    setThemeSetting,
  }
}
